import { FormikProps } from 'formik';
import { formatCurrency } from '../../helpers/stringHelpers';
import { ButtonIcon, SecondaryButton } from '../../shared/Button';
import { Header4 } from '../../shared/Headers';
import { Table, TBody, Td, Th, THead, Tr } from '../../shared/Table';
import { EditDesignBillOfMaterialsFormModel } from '../designs/edit-bill-of-materials/editDesignBillOfMaterialsFormModel';
import { ViewOrEditQuoteBillOfMaterialsFormModel } from '../quotes/view-or-edit-bill-of-materials/viewOrEditQuoteBillOfMaterialsFormModel';
import { itemIsAssistDesignCharge, itemIsTotalDesignCharge } from './billOfMaterialsItemFormModel';
import {
  ActionButtonTd,
  ActionButtonTh,
  CurrencyTd,
  CurrencyTh,
  PartCodeTh,
  UnitTd,
  UnitTh,
} from './BillOfMaterialsItemsTable';
import { BillOfMaterialsUnitFormModel } from './billOfMaterialsUnitFormModel';
import { BillOfMaterialsPartsById } from './dataForEditBillOfMaterialsForm';
import { ReactComponent as SwapIcon } from '../../images/icons/swap.svg';

type Props = {
  units: Array<BillOfMaterialsUnitFormModel>;
  partsById: BillOfMaterialsPartsById;
  setFieldValue: FormikProps<
    EditDesignBillOfMaterialsFormModel | ViewOrEditQuoteBillOfMaterialsFormModel
  >['setFieldValue'];
};

export const BillOfMaterialsDesignChargeField = ({ units, partsById, setFieldValue }: Props) => {
  const getUnselectedDesignChargeItem = (unit: BillOfMaterialsUnitFormModel) => {
    const unselectedDesignChargeItems =
      unit.underfloorHeatingItems.filter(
        item =>
          item.isDeleted && (itemIsAssistDesignCharge(item, partsById) || itemIsTotalDesignCharge(item, partsById)),
      ) || null;

    if (unselectedDesignChargeItems.length != 1) {
      throw new Error('Expected exactly one design charge item per unit');
    }

    return unselectedDesignChargeItems[0];
  };

  const findAssistDesignChargeItemIndex = (unit: BillOfMaterialsUnitFormModel) => {
    return unit.underfloorHeatingItems.findIndex(item => itemIsAssistDesignCharge(item, partsById));
  };

  const findTotalDesignChargeItemIndex = (unit: BillOfMaterialsUnitFormModel) => {
    return unit.underfloorHeatingItems.findIndex(item => itemIsTotalDesignCharge(item, partsById));
  };

  const setDesignChargeItemIsDeleted = (unitIndex: number, itemIndex: number, isDeleted: boolean) => {
    setFieldValue(`units.${unitIndex}.underfloorHeatingItems.${itemIndex}.isDeleted`, isDeleted);
  };

  return (
    <>
      <Header4>Design Charge Options</Header4>
      <Table>
        <THead>
          <Tr>
            <PartCodeTh>Part Code</PartCodeTh>
            <UnitTh>Unit</UnitTh>
            <Th>Description</Th>
            <CurrencyTh>Cost (£)</CurrencyTh>
            <CurrencyTh>Price (£)</CurrencyTh>
            <ActionButtonTh>
              <SecondaryButton
                title="Swap Design Charges"
                onClick={() => {
                  const firstUnitUnselectedDesignChargeItem = getUnselectedDesignChargeItem(units[0]);

                  const isAssistDesignChargeItemsDeleted = itemIsAssistDesignCharge(
                    firstUnitUnselectedDesignChargeItem,
                    partsById,
                  );

                  units.map((unit, index) => {
                    const assistDesignChargeItemIndex = findAssistDesignChargeItemIndex(unit);
                    const totalDesignChargeItemIndex = findTotalDesignChargeItemIndex(unit);

                    setDesignChargeItemIsDeleted(index, assistDesignChargeItemIndex, !isAssistDesignChargeItemsDeleted);
                    setDesignChargeItemIsDeleted(index, totalDesignChargeItemIndex, isAssistDesignChargeItemsDeleted);
                  });
                }}
              >
                <ButtonIcon>
                  <SwapIcon />
                </ButtonIcon>
              </SecondaryButton>
            </ActionButtonTh>
          </Tr>
        </THead>
        <TBody>
          {units.map((unit, unitIndex) => {
            const designChargeItem = getUnselectedDesignChargeItem(unit);

            const part = partsById[designChargeItem.partId];

            return (
              <Tr key={`design-charge-option-${unitIndex}`}>
                <Td>{part.name}</Td>
                <UnitTd>{unit.name}</UnitTd>
                <Td>{part.description}</Td>
                <CurrencyTd>
                  {designChargeItem.costInPounds && formatCurrency(designChargeItem.costInPounds)}
                </CurrencyTd>
                <CurrencyTd>
                  {designChargeItem.priceInPounds && formatCurrency(designChargeItem.priceInPounds)}
                </CurrencyTd>
                <ActionButtonTd />
              </Tr>
            );
          })}
        </TBody>
      </Table>
    </>
  );
};
